/**
 * @file useShopSettingsForm.ts
 * @description Данные и сохранение формы «Магазин» (phase19.31: SC52 — одна
 * кнопка «Сохранить» на всю форму; SC58 — часовой пояс магазина).
 *
 * Форма собирает три источника: настройки магазина (почта, пояс), список
 * поясов и «Заказы с сайта» (ответственный и кандидаты). Раньше почта
 * сохранялась кнопкой, а ответственный — сразу при выборе; теперь всё —
 * одним «Сохранить», неактивным без изменений. Отправляются только
 * изменённые части (два запроса — у них разные адреса на сервере).
 *
 * Ошибки сервера — у поля: `shop_timezone_invalid` под поясом, 400 по почте
 * под почтой; остальное — всплывающим сообщением человеческим текстом.
 * После смены пояса время в кабинете сразу идёт по новому: пояс ставится в
 * `shop-time`, а данные входа (`/auth/me`) перечитываются — каркас возьмёт
 * пояс из них при следующей отрисовке.
 */

import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../../auth/hooks";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { getCheckoutSettings, updateCheckoutSettings, type CheckoutSettingsDto } from "../../../lib/api-checkout-settings";
import {
  SHOP_TIMEZONE_INVALID,
  getShopSettings,
  listShopTimeZones,
  updateShopSettings,
  type ShopSettingsDto,
  type ShopTimeZoneOption,
} from "../../../lib/api-shop-settings";
import { setShopTimeZone } from "../../../lib/shop-time";

/** Изменяемые поля формы. Ответственный: пусто — «не назначен». */
export interface ShopSettingsDraft {
  contactEmail: string;
  timeZone: string;
  ownerId: string;
}

/** Ошибки у полей. */
export interface ShopSettingsErrors {
  contactEmail?: string;
  timeZone?: string;
}

/** Значения формы из ответов сервера. */
function draftOf(settings: ShopSettingsDto, checkout: CheckoutSettingsDto): ShopSettingsDraft {
  return {
    contactEmail: settings.contactEmail ?? "",
    timeZone: settings.timeZone,
    ownerId: checkout.defaultOrderOwner?.id ?? "",
  };
}

export function useShopSettingsForm() {
  const notify = useNotify();
  const { refresh } = useAuth();
  const [settings, setSettings] = useState<ShopSettingsDto | null>(null);
  const [checkout, setCheckout] = useState<CheckoutSettingsDto | null>(null);
  const [timeZones, setTimeZones] = useState<ShopTimeZoneOption[]>([]);
  const [draft, setDraft] = useState<ShopSettingsDraft | null>(null);
  const [errors, setErrors] = useState<ShopSettingsErrors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, zones] = await Promise.all([getShopSettings(), getCheckoutSettings(), listShopTimeZones()]);
      setSettings(s);
      setCheckout(c);
      setTimeZones(zones);
      setDraft(draftOf(s, c));
      setLoadError(null);
    } catch {
      setLoadError("Не удалось загрузить настройки магазина. Проверьте связь и попробуйте ещё раз.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saved = settings && checkout ? draftOf(settings, checkout) : null;
  const emailChanged = !!draft && !!saved && draft.contactEmail.trim() !== saved.contactEmail;
  const zoneChanged = !!draft && !!saved && draft.timeZone !== saved.timeZone;
  const ownerChanged = !!draft && !!saved && draft.ownerId !== saved.ownerId;
  const dirty = emailChanged || zoneChanged || ownerChanged;

  /** Правка поля снимает ошибку этого поля. */
  function change(part: Partial<ShopSettingsDraft>) {
    setDraft((prev) => (prev ? { ...prev, ...part } : prev));
    setErrors((prev) => ({
      contactEmail: "contactEmail" in part ? undefined : prev.contactEmail,
      timeZone: "timeZone" in part ? undefined : prev.timeZone,
    }));
  }

  async function saveShop(current: ShopSettingsDraft): Promise<boolean> {
    if (!emailChanged && !zoneChanged) return true;
    try {
      const updated = await updateShopSettings({
        ...(emailChanged ? { contactEmail: current.contactEmail.trim() } : {}),
        ...(zoneChanged ? { timeZone: current.timeZone } : {}),
      });
      setSettings(updated);
      if (zoneChanged) {
        // Время в кабинете — по новому поясу сразу, не дожидаясь /auth/me.
        setShopTimeZone(updated.timeZone);
        await refresh().catch(() => undefined);
      }
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.code === SHOP_TIMEZONE_INVALID) setErrors((e) => ({ ...e, timeZone: err.message }));
      else if (err instanceof ApiError && err.status === 400 && emailChanged) setErrors((e) => ({ ...e, contactEmail: err.message }));
      else notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить — изменения остались в форме. Попробуйте ещё раз.");
      return false;
    }
  }

  async function saveOwner(current: ShopSettingsDraft): Promise<boolean> {
    if (!ownerChanged) return true;
    try {
      setCheckout(await updateCheckoutSettings({ defaultOrderOwnerUserId: current.ownerId || null }));
      return true;
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить ответственного — изменения остались в форме.");
      return false;
    }
  }

  async function save() {
    if (!draft || !dirty) return;
    setSaving(true);
    const shopOk = await saveShop(draft);
    const ownerOk = await saveOwner(draft);
    setSaving(false);
    if (shopOk && ownerOk) notify.success("Настройки магазина сохранены");
  }

  return { settings, checkout, timeZones, draft, errors, loading, loadError, saving, dirty, change, save, reload: load };
}
